"use client";

/**
 * Brand shop — SERVICES tab (services-as-products, 2026-08).
 *
 * Beautiful service cards with a BOOK button where Buy normally sits:
 *  - service has a booking form attached → /f/<shareCode>?booking=<productId>
 *    (the form submission becomes a booking + service order server-side)
 *  - no form attached → WhatsApp deep link to the brand (pre-filled message)
 *
 * Data comes from the public `listWebsiteServices` query (active, unarchived,
 * website-visible service products only, each with what it comes with). The
 * card itself is ServiceCard, which a collection's page draws too.
 */

import { useQuery } from "convex/react";
import { api } from "@/convex/_generated/api";
import { Sparkles } from "lucide-react";
import type { Id } from "@/convex/_generated/dataModel";
import { ServiceCard, type PublicService } from "./ServiceCard";

interface BrandServicesSectionProps {
  brandId: string;
  storeId?: string;
  brandName: string;
  /** WhatsApp number for the no-form fallback (digits, any format). */
  whatsappNumber?: string;
  currency?: string;
}

export default function BrandServicesSection({
  brandId,
  storeId,
  brandName,
  whatsappNumber,
  currency = "UGX",
}: BrandServicesSectionProps) {
  const services = useQuery(
    api.products.websiteServices.listWebsiteServices,
    brandId
      ? {
          brandId: brandId as Id<"brands">,
          storeId: storeId ? (storeId as Id<"stores">) : undefined,
        }
      : "skip",
  ) as PublicService[] | undefined;

  return (
    <section id="services" className="max-w-7xl mx-auto px-4 sm:px-6 pb-16">
      <div className="flex items-center gap-2.5 mb-6">
        <Sparkles className="w-5 h-5" style={{ color: "#0f172a" }} />
        <h2 className="text-xl sm:text-2xl font-black uppercase tracking-wide" style={{ color: "#0f172a" }}>
          Services
        </h2>
        {services && (
          <span className="text-xs font-bold px-2 py-0.5 rounded-full" style={{ backgroundColor: "#f1f5f9", color: "#64748b" }}>
            {services.length}
          </span>
        )}
      </div>

      {services === undefined ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-5">
          {/* The cards' shape: a portrait photo, then the words. */}
          {[0, 1, 2].map((i) => (
            <div key={i} className="rounded-2xl overflow-hidden animate-pulse" style={{ border: "1px solid #e2e8f0" }}>
              <div className="aspect-[3/4]" style={{ backgroundColor: "#f1f5f9" }} />
              <div className="p-4 sm:p-5 space-y-2">
                <div className="h-4 w-2/3 rounded" style={{ backgroundColor: "#f1f5f9" }} />
                <div className="h-3 w-1/3 rounded" style={{ backgroundColor: "#f1f5f9" }} />
              </div>
            </div>
          ))}
        </div>
      ) : services.length === 0 ? (
        <p className="text-sm py-10 text-center" style={{ color: "#64748b" }}>
          No services available right now.
        </p>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-5">
          {services.map((svc) => (
            <ServiceCard
              key={svc._id}
              service={svc}
              brandName={brandName}
              whatsappNumber={whatsappNumber}
              currency={currency}
            />
          ))}
        </div>
      )}
    </section>
  );
}
